import { motion } from "framer-motion";
import { useI18n } from "@/context/I18nContext";

export default function Milestones() {
  const { t, dir } = useI18n();
  const isRtl = dir === "rtl";
  const m = t.milestones;

  return (
    <section
      id="milestones"
      className="relative bg-secondary text-secondary-foreground overflow-hidden"
      data-testid="milestones-section"
    >
      <div className="pointer-events-none absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            "linear-gradient(transparent 95%, currentColor 95%), linear-gradient(90deg, transparent 95%, currentColor 95%)",
          backgroundSize: "60px 60px",
        }}
      />

      <div className="container-page section-y relative">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-16">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-7"
          >
            <span className="overline">{m.eyebrow}</span>
            <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-black leading-[1.05]">
              {m.heading1} <br />
              <span className="text-primary">{m.heading1Accent}</span>
            </h2>
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-5 text-secondary-foreground/70 leading-relaxed"
            data-testid="milestones-description"
          >
            {m.description}
          </motion.p>
        </div>

        <div className="relative">
          {/* Vertical rail */}
          <div className={`absolute top-0 bottom-0 ${isRtl ? "right-4 md:right-6" : "left-4 md:left-6"} w-px bg-white/15`} />
          <motion.div
            initial={{ height: 0 }}
            whileInView={{ height: "100%" }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1.6, ease: "easeOut" }}
            className={`absolute top-0 ${isRtl ? "right-4 md:right-6" : "left-4 md:left-6"} w-px bg-primary`}
          />

          <ol className="space-y-10 md:space-y-14">
            {m.items.map((item, i) => (
              <motion.li
                key={i}
                initial={{ opacity: 0, x: isRtl ? 30 : -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.55, delay: i * 0.05 }}
                className={`relative grid md:grid-cols-12 gap-4 md:gap-8 ${isRtl ? "pr-14 md:pr-20" : "pl-14 md:pl-20"}`}
                data-testid={`milestone-${i}`}
              >
                {/* Year bubble on rail */}
                <span className={`absolute top-1 ${isRtl ? "right-0 md:right-0" : "left-0 md:left-0"} w-9 h-9 md:w-12 md:h-12 grid place-items-center bg-primary text-primary-foreground text-[10px] md:text-xs font-black tracking-tight tabular-nums`}>
                  {item.year}
                </span>

                <div className="md:col-span-4">
                  <div className="text-xs uppercase tracking-[0.22em] text-primary/80">{item.year}</div>
                  <h3 className="mt-2 text-xl md:text-2xl font-bold leading-tight">{item.title}</h3>
                </div>
                <div className="md:col-span-8">
                  <p className="text-sm md:text-base text-secondary-foreground/70 leading-relaxed max-w-2xl">
                    {item.text}
                  </p>
                  <div className="mt-4 h-px w-14 bg-white/15" />
                </div>
              </motion.li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
}
